import { useForm } from "react-hook-form";
import {
  ICustomerPriceRule,
  PriceCondition,
  CustomerPriceRuleType,
  RuleCondition,
} from "../../../models/PriceSettings";
import { TextInput } from "../../shared/Inputs/TextInput";
import SelectInput from "../../shared/Inputs/SelectInput";
import GeneralPriceSettingSingle from "../PriceConfiguration/GeneralPriceSettingSingle";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import usePriceSettingsMutations from "../../../data/usePriceSettingsMutations";
import { useEffect, useState } from "react";

interface IPriceRuleParams {
  customerPriceConfigurationId: string;
  priceRule?: ICustomerPriceRule;
  ruleName: string;
  ruleType: CustomerPriceRuleType;
  onSuccess?: any;
}

export default function PriceRuleSingle({
  customerPriceConfigurationId,
  priceRule,
  ruleName,
  ruleType,
  onSuccess,
}: IPriceRuleParams) {
  // The £/% toggle sets a boolean via setValue, but the hidden radio can also leave a "true"/"false" string.
  const toBool = (v: any) => v === true || v === "true";
  const { createPriceRule, updatePriceRule, removePriceRule } = usePriceSettingsMutations();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers(`${ruleName} Rule`);
  const [isLoading, setIsLoading] = useState(true);
  const { handleSubmit, register, setValue, watch, reset } = useForm<ICustomerPriceRule>({
    defaultValues: {
      id: priceRule?.id || "",
      customerPriceConfigurationId: customerPriceConfigurationId,
      value: priceRule?.value || 0,
      type: ruleType,
      condition: priceRule?.condition || PriceCondition.LessThan,

      priceSetting: {
        value: priceRule?.priceSetting?.value || 0,
        isFixed: priceRule?.priceSetting?.isFixed ?? true,
      },

      engineCondition: priceRule?.engineCondition || RuleCondition.All,
      bodyCondition: priceRule?.bodyCondition || RuleCondition.All,
    },
  });

  const enumArray = [
    ...Object.values(PriceCondition).filter((ct) => isNaN(Number(ct))),
  ];

  const options = enumArray.map((opt, i) => {
    return {
      id: i,
      name: opt
        .toString()
        .replace(/([A-Z])/g, " $1")
        .trim(),
    };
  });

  const ruleEnumArray = [
    ...Object.values(RuleCondition).filter((ct) => isNaN(Number(ct))),
  ];

  const ruleOptions = ruleEnumArray.map((opt, i) => {
    return {
      id: i,
      name: opt
        .toString()
        .replace(/([A-Z])/g, " $1")
        .trim(),
    };
  });

  const onSubmit = (data: any) => {
    if (priceRule) {
      updatePriceRule.mutate(
        {
          ...data,
          priceSetting: {
            ...data.priceSetting,
            value:
              data.priceSetting.value !== "" && data.priceSetting.value !== null
                ? parseFloat(data.priceSetting.value)
                : 0,
            isFixed: toBool(data.priceSetting.isFixed),
          },
        },
        {
          ...saveHandlers,
          onSuccess: () => {
            saveHandlers.onSuccess();
            onSuccess();
          },
          onError: (error) => {
            saveHandlers.onError();
            console.error(error);
          },
        }
      );
    } else {
      data.id = null;
      createPriceRule.mutate(
        {
          ...data,
          priceSetting: {
            ...data.priceSetting,
            value:
              data.priceSetting.value !== "" && data.priceSetting.value !== null
                ? parseFloat(data.priceSetting.value)
                : 0,
            isFixed: toBool(data.priceSetting.isFixed),
          },
          value: data.value !== "" && data.value !== null ? parseFloat(data.value) : "",
        },
        {
          ...saveHandlers,
          onSuccess: () => {
            saveHandlers.onSuccess();
            onSuccess();
            reset();
          },
          onError: (error) => {
            saveHandlers.onError();
            console.error(error);
          },
        }
      );
    }
  };

  const onRemove = () => {
    if (priceRule) {
      removePriceRule.mutate(priceRule.id, {
        ...deleteHandlers,
        onSuccess: () => {
          deleteHandlers.onSuccess();
          onSuccess();
        },
      });
    }
  };

  useEffect(() => {
    if (priceRule && isLoading) {
      reset(priceRule);
      setIsLoading(false);
    } else if (!priceRule && isLoading) {
      setIsLoading(false);
    }
  }, [priceRule]);

  if (isLoading) return null;


  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-4">
      <div className="items-center gap-x-4 grid grid-cols-1 md:grid-cols-4">
        <SelectInput
          label="Engine Condition"
          register={register}
          registerName={`engineCondition`}
          options={ruleOptions}
          setValue={setValue}
          watch={watch}
          cols="w-64"
        />
        <SelectInput
          label="Body Condition"
          register={register}
          registerName={`bodyCondition`}
          options={ruleOptions}
          setValue={setValue}
          watch={watch}
          cols="w-64"
        />
        <SelectInput
          label="Condition"
          register={register}
          registerName={`condition`}
          options={options}
          setValue={setValue}
          watch={watch}
          cols="w-64"
        />
        <TextInput
          label={"Value"}
          registerName={`value`}
          register={register}
          placeholder="Make, Model, Year etc. "
          cols="w-64"
        />
      </div> 
      <div className="items-center gap-x-4 grid grid-cols-1 md:grid-cols-4">
        <SelectInput
          label="Year Condition"
          register={register}
          registerName={`yearCondition`}
          options={options}
          setValue={setValue}
          watch={watch}
          cols="w-64"
        />
        <TextInput
          label={"Year Value"}
          registerName={`yearValue`}
          register={register}
          placeholder="Make, Model, Year etc. "
          cols="w-64"
        />
        
        {/* divider */}
        {/* <div className="border-t-2 border-dashed border-primary-600 w-10 mt-7"></div> */}

        <div className="flex flex-col gap-y-4 md:col-span-2">
          <GeneralPriceSettingSingle
            label={"Price Change"}
            objectName={"priceSetting"}
            register={register}
            setValue={setValue}
            watch={watch}
            minVal={null}
            
          />
        </div>

        <div className="flex grow mt-[28px] space-x-4">
          <ButtonInput label={priceRule ? "Save" : "Create"} isSubmit />
          {priceRule && (
            <ButtonInput
              label="Delete"
              isSubmit={false}
              onClick={onRemove}
              classes="bg-red-600 hover:bg-red-600/80"
            />
          )}
        </div>
      </div>
    </form>
  );
}
